import React, { useEffect, useState } from 'react';
import { FRTSearchForm } from 'pages/Firearms/FRTSearchForm';
import { ISearchFormValues } from 'pages/Firearms/RegisteredFirearmsSearchForm';
import { Loader } from 'components/atoms/Loader';
import { Table } from 'components/molecules/Table';
import {
  ESearchFRTContextType,
  FRTTableColumns,
  mapFRTToDataSource,
} from 'pages/Firearms/helpers';
import { CardList } from 'components/molecules/CardList';
import { FRTCard } from 'pages/Firearms/FRTCard';
import { Pagination } from 'components/molecules/Pagination';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { EPerPages, ISortOptions } from 'models/table';
import { useFirearms } from 'pages/Firearms/hooks';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { StyledPageTitle } from 'theme/styles';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'hooks/useQuery';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import Grid from '@mui/material/Grid';
import { ISingleFRT } from 'models/firearm';
import { useTranslations } from 'hooks/useTranslations';
import { areArraysEqual } from 'utils/arrays';
import { useLang } from 'models/langContext';
import { useDictionary } from 'models/dictionaryContext';

const SearchForFRTFirearmPage = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;
  const { isDesktop } = useThemeBreakpoint();
  const [isFRTLoading, setIsFRTLoading] = useState<boolean>(false);
  const [FRTSort, setFRTSort] = useState<ISortOptions | null>(null);
  const [queryFRT, setQueryFRT] = useState<Partial<ISearchFormValues>>({});
  const [perPageFRT, setPerPageFRT] = useState<EPerPages>(EPerPages.perPage25);
  const [currentPageFRT, setCurrentPageFRT] = useState<number>(1);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const { FRT, getFRTData, paginatorFRT } = useFirearms();
  const { state: routerState }: any = useLocation();
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();

  const {
    getMake,
    make,
    getModel,
    model,
    getManufacturer,
    manufacturer,
    getFirearmAction,
    firearmAction,
    getFirearmType,
    firearmType,
    getManufacturerCountry,
    manufacturerCountry,
  } = useDictionary();

  useEffect(() => {
    (async function initDictionaries() {
      getMake();
      getModel();
      getManufacturerCountry();
      getFirearmAction();
      getFirearmType();
      getManufacturer();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async function getFRTDataInit() {
      setIsPaginationLoading(true);
      if (!FRT.length) {
        setIsFRTLoading(true);
      }
      await getFRTData(currentPageFRT, perPageFRT, queryFRT, FRTSort);
      setIsFRTLoading(false);
      setIsPaginationLoading(false);
    })();
  }, [currentPageFRT, perPageFRT, queryFRT, FRTSort]);

  const handleSubmit = (values: ISearchFormValues) => {
    if (anyValueDiffersFromQuery(values)) {
      setQueryFRT(values);
      setCurrentPageFRT(1);
    }
  };

  const anyValueDiffersFromQuery = (values: ISearchFormValues) =>
    !areArraysEqual(values.makesUniqueIds, queryFRT.makesUniqueIds) ||
    !areArraysEqual(values.modelsUniqueIds, queryFRT.modelsUniqueIds) ||
    !areArraysEqual(values.manufacturersUniqueIds, queryFRT.manufacturersUniqueIds) ||
    !areArraysEqual(values.countriesUniqueIds, queryFRT.countriesUniqueIds) ||
    values.productCode !== queryFRT.productCode ||
    !areArraysEqual(values.actionsUniqueIds, queryFRT.actionsUniqueIds) ||
    !areArraysEqual(values.typesUniqueIds, queryFRT.typesUniqueIds) ||
    !areArraysEqual(values.calibersUniqueIds, queryFRT.calibersUniqueIds) ||
    values.isLocal !== queryFRT.isLocal;

  return (
    <Grid>
      <Breadcrumbs
        items={
          routerState?.ancillaries
            ? [
                {
                  label: t('addFirearm.addAncillaries.breadcrumb'),
                  route: ERouteLinks.AddAncillaries,
                },
                t('addFirearm.searchForFrtFirearm.breadcrumb'),
              ]
            : [
                {
                  label: t('addFirearm.legalEntities.breadcrumb'),
                  route: ERouteLinks.LegalEntities,
                },
                {
                  label:
                    type === ELegalEntityTypes.individual
                      ? t('addFirearm.individual.breadcrumb')
                      : t('addFirearm.organisation.breadcrumb'),
                  route: `${ERouteLinks.LegalEntities}?expand=${
                    type === ELegalEntityTypes.individual ? 'individual' : 'organisation'
                  }`,
                },
                {
                  label: t('addFirearm.legalEntityDetails.breadcrumb'),
                  route: `${ERouteLinks.ViewLegalEntity.replace(
                    ':id',
                    id,
                  )}?type=${type}&expand=firearms`,
                },
                {
                  label: t('addFirearm.addFirearm.breadcrumb'),
                  route: `${ERouteLinks.LegalEntityAddFirearm.replace(
                    ':id',
                    id,
                  )}?type=${type}&expand=firearms`,
                },
                t('addFirearm.searchForFrtFirearm.breadcrumb'),
              ]
        }
      />

      <StyledPageTitle variant="h4">
        {t('searchforFRTfirearm.searchforFRTfirearm.header')}
      </StyledPageTitle>

      <Button
        sx={{ margin: '16px 0' }}
        label={t('searchforFRTfirearm.back.button')}
        size={EButtonSizes.small}
        variant={EButtonVariants.outlined}
        onClick={() =>
          navigate(
            routerState?.ancillaries
              ? ERouteLinks.AddAncillaries
              : `${ERouteLinks.LegalEntityAddFirearm.replace(
                  ':id',
                  id as string,
                )}?type=${type}&expand=firearms`,
            { state: { ...routerState } },
          )
        }
        icon={EIconTypes.left}
      />

      <FRTSearchForm
        isSearchButtonDisabled={isFRTLoading}
        onSubmit={handleSubmit}
        showSaveMyFilter={false}
        isTransactionContext={true}
        isSearchForFRTContext={true}
      />
      {isFRTLoading ? (
        <Loader isLoading={isFRTLoading} />
      ) : (
        <Grid container sx={{ mt: 2 }}>
          {isDesktop ? (
            <Table
              columns={FRTTableColumns(
                {
                  handleSelect: (firearm: ISingleFRT) => {
                    navigate(
                      routerState?.ancillaries
                        ? ERouteLinks.AddAncillaries
                        : `${ERouteLinks.FRTFirearmDetails.replace(
                            ':id',
                            id as string,
                          ).replace(':frtId', firearm.id)}?type=${type}&expand=firearms`,
                      {
                        state: {
                          ...routerState,
                          firearm,
                          prevRoute: 'prevRoute',
                          isLocal: !!queryFRT?.isLocal,
                        },
                      },
                    );
                  },
                },
                ESearchFRTContextType.LegalEntities,
              )}
              dataSource={mapFRTToDataSource(
                FRT,
                make,
                model,
                manufacturer,
                firearmAction,
                firearmType,
                manufacturerCountry,
              )}
              onSort={setFRTSort}
            />
          ) : (
            <CardList
              items={mapFRTToDataSource(
                FRT,
                make,
                model,
                manufacturer,
                firearmAction,
                firearmType,
                manufacturerCountry,
              )}
              render={(data, index) => (
                <FRTCard
                  data={data}
                  key={index}
                  handleSelect={() =>
                    navigate(
                      routerState?.ancillaries
                        ? ERouteLinks.AddAncillaries
                        : `${ERouteLinks.FRTFirearmDetails.replace(
                            ':id',
                            id as string,
                          ).replace(':frtId', data.id)}?type=${type}&expand=firearms`,
                      {
                        state: {
                          ...routerState,
                          firearm: data,
                          prevRoute: 'prevRoute',
                          isLocal: !!queryFRT?.isLocal,
                        },
                      },
                    )
                  }
                />
              )}
            />
          )}

          {paginatorFRT && (
            <Pagination
              count={paginatorFRT?.totalElements}
              perPage={perPageFRT}
              onChangePage={(page) => {
                setCurrentPageFRT(page);
              }}
              onChangePerPage={(value) => {
                if (value.toString() !== perPageFRT.toString()) {
                  setPerPageFRT(value);
                }
              }}
              current={currentPageFRT}
              isLoading={isPaginationLoading}
            />
          )}
        </Grid>
      )}
    </Grid>
  );
};

export default SearchForFRTFirearmPage;
